import { useEffect, useState } from 'react';
import Image from '@/components/Image';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Noticia } from '@/lib/types';
import ReactMarkdown from 'react-markdown';
import { Loader2, Megaphone, Calendar } from 'lucide-react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { sanitizeFirestoreData } from '@/lib/utils';
import { motion } from 'framer-motion';

export default function NoticiasPage() {
  const [noticias, setNoticias] = useState<Noticia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNoticias = async () => {
      try {
        const q = query(collection(db, 'noticias'), orderBy('date', 'desc'));
        const snapshot = await getDocs(q);
        const noticiasData = snapshot.docs.map((doc) => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as Noticia[];
        setNoticias(noticiasData);
      } catch (err) {
        console.error(err);
        setError('No se pudieron cargar las noticias.');
      } finally {
        setLoading(false);
      }
    };
    fetchNoticias();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="relative overflow-hidden border-b border-white/8 py-20 text-center">
        <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
        <div className="container relative mx-auto max-w-3xl px-4">
          <motion.div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Megaphone className="h-7 w-7 text-primary" />
          </motion.div>
          <motion.h1
            className="font-headline text-5xl font-bold md:text-7xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">Últimas Noticias</span>
          </motion.h1>
          <motion.p
            className="mt-4 text-base text-muted-foreground md:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Mantente al día con los últimos acontecimientos, anuncios y eventos que suceden en el mundo de Jartiland.
          </motion.p>
        </div>
      </header>

      <main className="container mx-auto max-w-4xl px-4 py-16">
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
          </div>
        ) : error ? (
          <p className="text-center text-destructive">{error}</p>
        ) : noticias.length > 0 ? (
          <div className="space-y-6">
            {noticias.map((noticia, index) => (
              <motion.article
                key={noticia.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: Math.min(index, 6) * 0.05 }}
                whileHover={{ y: -4 }}
                className="group relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 backdrop-blur-sm transition-colors hover:border-primary/30"
              >
                <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-amber-500 opacity-0 transition-opacity group-hover:opacity-100" />

                {noticia.imageUrl && (
                  <div className="relative aspect-video w-full overflow-hidden">
                    <Image
                      src={noticia.imageUrl}
                      alt={`Imagen para la noticia: ${noticia.title}`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      data-ai-hint="news article"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  </div>
                )}

                <div className="p-6 md:p-8">
                  <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                    <Calendar className="h-3.5 w-3.5" />
                    {noticia.date ? format(new Date(noticia.date), "dd 'de' MMMM, yyyy", { locale: es }) : ''}
                  </div>
                  <h2 className="font-headline text-2xl font-bold leading-tight md:text-3xl">{noticia.title}</h2>
                  <div className="prose prose-sm prose-invert mt-4 max-w-none text-muted-foreground">
                    <ReactMarkdown>{noticia.content}</ReactMarkdown>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        ) : (
          <div className="py-20 text-center">
            <h2 className="text-2xl font-semibold">No hay noticias por ahora</h2>
            <p className="mt-2 text-muted-foreground">Vuelve a consultar más tarde para ver las últimas actualizaciones.</p>
          </div>
        )}
      </main>
    </div>
  );
}
